import { Timestamp } from "firebase/firestore";
import { firestoreTimestampToDate, isDate } from "../utils";

export const SIGHTINGS_MODULE = 'sightings';

export type SightingType = 'wildSighting' | 'wildActivity' | 'damage';

export const SIGHTING_TYPES: { value: SightingType; labelKey: string; iconName: string }[] = [
  { value: 'wildSighting', labelKey: 'COCKPIT.SIGHTINGS.type.wildSighting', iconName: 'wildlife-sighting' },
  { value: 'wildActivity', labelKey: 'COCKPIT.SIGHTINGS.type.wildActivity', iconName: 'wildlife-activity' },
  { value: 'damage',       labelKey: 'COCKPIT.SIGHTINGS.type.damage',       iconName: 'damage-anomaly' },
];

export interface ISpeciesSighting {
  species: string;
  count: number;
  gender?: string | undefined;
  ageClass?: string | undefined;
}

export const ACTIVITY_TYPES: { value: string; labelKey: string }[] = [
  { value: 'rubbingTree',   labelKey: 'COCKPIT.SIGHTINGS.activityType.rubbingTree' },
  { value: 'scrape',        labelKey: 'COCKPIT.SIGHTINGS.activityType.scrape' },
  { value: 'bedding',       labelKey: 'COCKPIT.SIGHTINGS.activityType.bedding' },
  { value: 'burrow',        labelKey: 'COCKPIT.SIGHTINGS.activityType.burrow' },
  { value: 'feedingSite',   labelKey: 'COCKPIT.SIGHTINGS.activityType.feedingSite' },
  { value: 'diggingSite',   labelKey: 'COCKPIT.SIGHTINGS.activityType.diggingSite' },
  { value: 'dropping',      labelKey: 'COCKPIT.SIGHTINGS.activityType.dropping' },
  { value: 'footprint',     labelKey: 'COCKPIT.SIGHTINGS.activityType.footprint' },
  { value: 'wildlifeTrail', labelKey: 'COCKPIT.SIGHTINGS.activityType.wildlifeTrail' },
  { value: 'other',         labelKey: 'COCKPIT.SIGHTINGS.activityType.other' },
];

export const DAMAGE_TYPES: { value: string; labelKey: string }[] = [
  { value: 'browsing',      labelKey: 'COCKPIT.SIGHTINGS.damageType.browsing' },
  { value: 'barkStripping', labelKey: 'COCKPIT.SIGHTINGS.damageType.barkStripping' },
  { value: 'crop',          labelKey: 'COCKPIT.SIGHTINGS.damageType.crop' },
  { value: 'diggingDamage', labelKey: 'COCKPIT.SIGHTINGS.damageType.diggingDamage' },
  { value: 'injuredAnimal', labelKey: 'COCKPIT.SIGHTINGS.damageType.injuredAnimal' },
  { value: 'sickAnimal',    labelKey: 'COCKPIT.SIGHTINGS.damageType.sickAnimal' },
  { value: 'other',         labelKey: 'COCKPIT.SIGHTINGS.damageType.other' },
];

export interface ISighting {
  identifier?: string;
  contextId: string;
  contextIds?: string[]|undefined;

  sightingType: SightingType;
  contextIdRights?: {[contextId: string]: 'view'|'update'}|undefined;

  location?: { latitude: number; longitude: number } | undefined;
  huntingDistrictId?: string | undefined;
  huntingDistrictName?: string | undefined;

  /** (Suspected) species observed — all three sighting types use this. */
  species?: ISpeciesSighting[];

  /** wildSighting only. */
  behaviour?: string | undefined;
  /** wildActivity only — one of ACTIVITY_TYPES. */
  activityType?: string | undefined;
  /** damage only — one of DAMAGE_TYPES. */
  damageType?: string | undefined;
  /** All types — free text (health condition / freshness / severity, depending on type). */
  condition?: string | undefined;

  note?: string | undefined;

  userId: string;
  userDisplayName: string;

  creationDate?: Date | Timestamp | undefined;
  updateTimestamp?: Date | Timestamp | undefined;
}

export class Sighting {
  public static className = "Sighting"
  public className = "Sighting"
  contextIds?: string[]|undefined = undefined
  contextIdRights?: {[contextId: string]: 'view'|'update'}|undefined = undefined
  identifier?: string|undefined = undefined;
  contextId: string|undefined = undefined;
  sightingType: SightingType|undefined = undefined;
  location?: { latitude: number; longitude: number } | undefined = undefined;
  huntingDistrictId?: string | undefined = undefined;
  huntingDistrictName?: string | undefined = undefined;
  /** (Suspected) species observed — all three sighting types use this. */
  species?: ISpeciesSighting[] | undefined = undefined;
  /** wildSighting only. */
  behaviour?: string | undefined = undefined;
  /** wildActivity only — one of ACTIVITY_TYPES. */
  activityType?: string | undefined = undefined;
  /** damage only — one of DAMAGE_TYPES. */
  damageType?: string | undefined = undefined;
  /** All types — free text (health condition / freshness / severity, depending on type). */
  condition?: string | undefined = undefined;
  note?: string | undefined = undefined;
  userId: string|undefined = undefined;
  userDisplayName: string|undefined = undefined;
  creationDate?: Date | Timestamp | undefined = undefined;
  updateTimestamp?: Date | Timestamp | undefined = undefined;

  constructor( ) {}

  setFromAny(element: any) {
    const {identifier, contextIdRights, contextIds, contextId, sightingType, location, huntingDistrictId, huntingDistrictName,
    species, behaviour, activityType, damageType, condition, note, userId, userDisplayName, creationDate, updateTimestamp } = element
  
    this.contextIds = contextIds ? contextIds : this.contextIds
    this.contextIdRights = contextIdRights ? contextIdRights : this.contextIdRights
    this.identifier = identifier ? identifier : this.identifier
    this.contextId = contextId ? contextId : this.contextId
    this.sightingType = sightingType ? sightingType : this.sightingType
    this.location = location ? location : this.location
    this.huntingDistrictId = huntingDistrictId ? huntingDistrictId: this.huntingDistrictId
    this.huntingDistrictName = huntingDistrictName ? huntingDistrictName : this.huntingDistrictName
    this.species = species ? species : this.species
    this.behaviour = behaviour ? behaviour : this.behaviour
    this.activityType = activityType ? activityType : this.activityType
    this.damageType = damageType ? damageType : this.damageType
    this.condition = condition ? condition : this.condition
    this.note = note ? note : this.note
    this.userId = userId ? userId : this.userId
    this.userDisplayName = userDisplayName ? userDisplayName : this.userDisplayName
    this.creationDate = creationDate ? creationDate : this.creationDate
    this.updateTimestamp = updateTimestamp ? isDate(updateTimestamp) ? updateTimestamp : firestoreTimestampToDate(updateTimestamp) : this.updateTimestamp
  
  }

  searchFilter(filter: string): boolean {
    const str = `${this.note}${this.huntingDistrictName}${this.identifier}${this.behaviour}${this.location}${this.condition}`.toLowerCase()
    return str.includes(filter)
  }

  /**
     * Function for skipping objects and data while deepcopying class.
     * used as optional Input for deepCopy(any, replacerFn)
     * @param key 
     */
  replacerFn(key: string) {
    if (key == "className") return false
    else return true
} 
}

export function sightingTypeIconName(type: SightingType | null | undefined): string {
  return SIGHTING_TYPES.find(t => t.value === type)?.iconName ?? 'pin';
}

/** `species.species` stores a species identifier (same convention as GameRecord's
 * `wildart`) — pass a lookup to resolve it to a display name, e.g. built from
 * `context.settingsMap.get(SETTING_SPECIES).values`. */
export function sightingSpeciesSummary(species?: ISpeciesSighting[] | null, nameById?: Map<string, string>): string {
  return (species ?? [])
    .map(s => `${s.count ?? 1} ${nameById?.get(s.species) ?? s.species}`)
    .join(', ');
}

export interface ISightingImage {
  docId?: string;
  identifier: string;
  parentId?: string;
  contextId?: string;
  thumbnailData?: any;
  creationDate?: Date | Timestamp;
  updateTimestamp?: Date | Timestamp;
  createdByUserId?: string;
  createdByUserName?: string;
}